import { Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import { BehaviorSubject, firstValueFrom, type Observable } from 'rxjs';
import { isNativeApp } from '../auth/native-auth';
import { DeviceTokenService } from './device-token.service';
import { MessageUnreadService } from '../../features/messages/shared/message-unread.service';
import { AppInsightsService } from './app-insights.service';
import type { PermissionState } from '@capacitor/core';

/**
 * DI token for the Capacitor `PushNotifications` plugin. Injecting the plugin
 * (instead of calling it directly) keeps `NativePushService` unit-testable: the
 * real plugin proxy is provided in production while tests substitute a mock.
 */
export const PUSH_NOTIFICATIONS = new InjectionToken<typeof PushNotifications>(
  'PUSH_NOTIFICATIONS',
  { providedIn: 'root', factory: () => PushNotifications },
);

/**
 * Snapshot of native push state, consumed by `PushNotificationService` (which
 * maps it into the web-shaped `SubscriptionStatus`) and, indirectly, by the
 * notification-settings UI.
 */
export interface NativePushStatus {
  /** OS notification permission. `prompt` means "not decided yet". */
  permission: PermissionState;
  /** True once an FCM token has been registered with the backend. */
  registered: boolean;
  /** True only inside the Capacitor native shell. */
  supported: boolean;
  error?: string;
}

/**
 * Owns ALL native (Capacitor Android/iOS) push-notification behaviour.
 *
 * This service mirrors the proven `NativeAuthService` pattern: it is a complete
 * no-op on the web (`init()` returns immediately when not running natively, and
 * every method is guarded on {@link isNativeApp}), and every Capacitor plugin
 * callback is re-entered into Angular's zone via `NgZone.run` before it touches
 * the router, HTTP, or observable state (plugin callbacks fire OUTSIDE the zone,
 * exactly like the `appUrlOpen` handler in `NativeAuthService`).
 *
 * Responsibilities:
 *  - request/track the OS notification permission (Android 13+ POST_NOTIFICATIONS,
 *    iOS user-authorization prompt),
 *  - obtain the **FCM registration token** (APNs→FCM on iOS) and register it with
 *    the backend as the NATIVE contract shape — a bare token string with
 *    `device_type: 'android'` or `'ios'`, chosen from `Capacitor.getPlatform()`
 *    — see `contracts/device-token-registration.md`,
 *  - refresh the unread-message badge when a push arrives in the foreground,
 *  - deep-link a tapped push into `/messages/:id`.
 *
 * The browser PWA continues to use the Web Push / VAPID path in
 * `PushNotificationService`; the two never overlap.
 */
@Injectable({ providedIn: 'root' })
export class NativePushService {
  private deviceTokenService = inject(DeviceTokenService);
  private messageUnread = inject(MessageUnreadService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private push = inject(PUSH_NOTIFICATIONS);
  private appInsights = inject(AppInsightsService);
  private initialized = false;

  /**
   * Serialized detail of the most recent `registrationError` payload, captured
   * on the listener so the enable() outcome telemetry can include the actual
   * APNs/FCM failure reason (the underlying `err` is otherwise only visible in
   * an on-device `console.error` that never reaches App Insights).
   */
  private lastRegistrationErrorDetail: string | null = null;

  /**
   * Resolver for an in-flight `enable()` awaiting the registration outcome.
   * `register()` only *kicks off* token retrieval; the real result (backend
   * `/device-tokens` success, or a registration/token error) arrives later on
   * the plugin listeners, which settle this to reflect true push readiness.
   */
  private pendingRegistration:
    | ((outcome: { success: boolean; error?: string }) => void)
    | null = null;

  /**
   * Number of `registration` events received this session, and the value of the most
   * recent one. Both exist ONLY to give the `native_push_token_received` telemetry a
   * provenance dimension: the native event payload carries just `{ value }`, so
   * "explicit re-delivery" vs "first passive delivery" can only be derived here.
   *
   * A repeat delivery is expected and harmless — see {@link onTokenReceived}. On iOS the
   * AppDelegate deliberately does NOT de-duplicate the explicit
   * `register()` -> `didRegisterForRemoteNotificationsWithDeviceToken` path, because a
   * `registration` event fired before Angular attaches its listeners is silently dropped
   * by Capacitor (`notifyListeners` defaults to `retainUntilConsumed: NO`).
   *
   * `lastReceivedToken` is never logged: it is a device credential.
   */
  private tokenDeliveryCount = 0;
  private lastReceivedToken: string | null = null;

  /** How long `enable()` waits for the async registration outcome. */
  private static readonly REGISTRATION_TIMEOUT_MS = 15000;

  private readonly status = new BehaviorSubject<NativePushStatus>({
    permission: 'prompt',
    registered: false,
    supported: isNativeApp(),
  });
  readonly status$: Observable<NativePushStatus> = this.status.asObservable();

  get snapshot(): NativePushStatus {
    return this.status.value;
  }

  /**
   * Wire up the push listeners and reconcile the current permission. Safe to
   * call multiple times; only the first call on a native platform does work.
   * Invoked from `AppComponent.ngOnInit`, alongside `NativeAuthService.init()`.
   */
  async init(): Promise<void> {
    if (!isNativeApp() || this.initialized) {
      return;
    }
    this.initialized = true;

    await this.push.addListener('registration', (token: Token) => {
      this.zone.run(() => {
        // Breadcrumb: a token arrived from APNs/FCM. Never log the value.
        //
        // `deliveryIndex`/`isRepeatToken` make the iOS launch-order race diagnosable in
        // App Insights: on a warm launch the first delivery is dropped by Capacitor
        // before Angular attaches its listeners, so the delivery that actually lands
        // here is the explicit `register()` re-delivery of the SAME token. Seeing
        // `deliveryIndex: 1` on a warm launch is proof the explicit path delivered.
        this.tokenDeliveryCount++;
        const isRepeatToken = !!token?.value && token.value === this.lastReceivedToken;
        this.lastReceivedToken = token?.value ?? null;
        this.appInsights.trackEvent('native_push_token_received', {
          platform: Capacitor.getPlatform(),
          hasToken: !!token?.value,
          tokenLength: token?.value ? token.value.length : 0,
          deliveryIndex: this.tokenDeliveryCount,
          isRepeatToken,
          hasPendingEnable: !!this.pendingRegistration,
        });
        void this.onTokenReceived(token.value);
      });
    });

    await this.push.addListener('registrationError', (err: unknown) => {
      this.zone.run(() => {
        const serialized = this.serializeUnknownError(err);
        this.lastRegistrationErrorDetail = serialized.detail;
        console.error('[NativePush] registration error', err);
        // CRITICAL diagnostic: surface the underlying APNs/FCM failure reason to
        // App Insights (this is the failure we are hunting on iOS build 1010).
        this.appInsights.trackEvent('native_push_registration_error', {
          platform: Capacitor.getPlatform(),
          errorMessage: serialized.message ?? null,
          errorCode: serialized.code ?? null,
          errorDetail: serialized.detail,
        });
        this.status.next({ ...this.status.value, error: 'registration-error' });
        this.settleRegistration({ success: false, error: 'registration-error' });
      });
    });

    // Foreground receive: the OS does NOT post a tray notification while the app
    // is open, so refresh the badge so the unread count updates live.
    await this.push.addListener(
      'pushNotificationReceived',
      (_notification: PushNotificationSchema) => {
        this.zone.run(() => {
          console.log('[NativePush] push received (foreground) → refreshing unread badge');
          this.messageUnread.refresh();
        });
      },
    );

    // Tap: deep-link into the specific message.
    await this.push.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.zone.run(() => this.onActionPerformed(action));
      },
    );

    // Reflect the current OS permission. If already granted, re-register on
    // every launch so the backend always holds a fresh FCM token.
    try {
      const perm = await this.push.checkPermissions();
      this.setPermission(perm.receive);
      if (perm.receive === 'granted') {
        this.trackRegisterInvoked('init');
        await this.push.register();
      }
    } catch (err) {
      console.error('[NativePush] init permission check failed', err);
    }
  }

  /**
   * Request the OS notification permission (Android 13+ prompt) and, if granted,
   * trigger registration. Resolves only once the outcome is known: the FCM token
   * arrives asynchronously on the `registration` listener, which performs the
   * backend `/device-tokens` call — so the returned `success` reflects real push
   * readiness, not merely that `register()` was invoked.
   */
  async enable(): Promise<{ success: boolean; error?: string }> {
    if (!isNativeApp()) {
      this.trackEnableOutcome('not-native', { permissionState: null });
      return { success: false, error: 'not-native' };
    }

    let permissionState: PermissionState | null = null;
    try {
      await this.init();
      let perm = await this.push.checkPermissions();
      permissionState = perm.receive;
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await this.push.requestPermissions();
        permissionState = perm.receive;
      }
      this.setPermission(perm.receive);

      if (perm.receive !== 'granted') {
        this.trackEnableOutcome('permission-denied', { permissionState });
        return { success: false, error: 'permission-denied' };
      }

      // Reset any stale registration-error detail before a fresh attempt.
      this.lastRegistrationErrorDetail = null;
      // Arm the waiter BEFORE register() so a fast registration callback can't
      // settle before we're listening for it.
      const outcome = this.awaitRegistrationOutcome();
      this.trackRegisterInvoked('enable');
      await this.push.register();
      const result = await outcome;
      this.trackEnableOutcome(result.success ? 'success' : (result.error ?? 'enable-failed'), {
        permissionState,
      });
      return result;
    } catch (err) {
      console.error('[NativePush] enable failed', err);
      this.trackEnableOutcome('enable-failed', { permissionState, error: err });
      return { success: false, error: 'enable-failed' };
    }
  }

  /**
   * Disable push on this device: invalidate the registered native token(s) on
   * the backend so no further pushes are delivered here. Local OS permission is
   * left untouched (the user manages that in system settings).
   */
  async disable(): Promise<boolean> {
    if (!isNativeApp()) {
      return false;
    }

    try {
      await this.invalidateNativeTokens();
      this.status.next({ ...this.status.value, registered: false });
      return true;
    } catch (err) {
      console.error('[NativePush] disable failed', err);
      return false;
    }
  }

  /**
   * Register an FCM token with the backend and settle any pending `enable()`.
   *
   * This may run MORE THAN ONCE per session with the SAME token value, and that is by
   * design: the iOS AppDelegate always delivers the token in response to an explicit
   * `register()`, because a `registration` event emitted before Angular attached its
   * listeners is silently dropped by Capacitor and can never be replayed.
   *
   * Repeat deliveries are safe — `/device-tokens` is an upsert, and re-POSTing keeps the
   * backend record fresh. Do NOT add a "same token, already registered" short-circuit
   * that returns early: skipping {@link settleRegistration} here would strand a pending
   * `enable()` until its 15s timeout and recreate, in TypeScript, exactly the native
   * de-duplication bug this path was fixed to avoid. If such a short-circuit is ever
   * added it MUST still call `settleRegistration({ success: true })`.
   */
  private async onTokenReceived(fcmToken: string): Promise<void> {
    if (!fcmToken) {
      return;
    }
    // Discriminate the native surface so the token registers under the correct
    // `device_type` on the FROZEN `/device-tokens` contract (spec 033): iOS
    // sends an APNs→FCM token as `device_type: 'ios'`, Android as `'android'`.
    // Capacitor.getPlatform() returns 'ios' | 'android' | 'web'; anything that
    // is not iOS falls back to 'android' (this branch only runs inside the
    // native shell, so 'web' never reaches here).
    const deviceType: 'android' | 'ios' =
      Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    // Never log any part of the token — it is a device credential (constitution:
    // no secrets/tokens in logs).
    console.log('[NativePush] FCM token received, registering with backend');
    try {
      const request = this.deviceTokenService.createNativeDeviceToken(fcmToken, deviceType);
      await firstValueFrom(this.deviceTokenService.createDeviceToken(request));
      this.status.next({ ...this.status.value, registered: true, error: undefined });
      this.settleRegistration({ success: true });
      console.log(`[NativePush] device token registered with backend (device_type=${deviceType})`);
    } catch (err) {
      console.error('[NativePush] token registration failed', err);
      this.status.next({ ...this.status.value, error: 'token-registration-failed' });
      this.settleRegistration({ success: false, error: 'token-registration-failed' });
    }
  }

  /**
   * Return a promise that settles when the next registration attempt completes
   * (token registered with the backend, or a registration/token error), or when
   * {@link REGISTRATION_TIMEOUT_MS} elapses. Only one outcome is delivered.
   */
  private awaitRegistrationOutcome(): Promise<{ success: boolean; error?: string }> {
    return new Promise(resolve => {
      let settled = false;
      const done = (outcome: { success: boolean; error?: string }): void => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        this.pendingRegistration = null;
        resolve(outcome);
      };
      const timer = setTimeout(
        () => done({ success: false, error: 'registration-timeout' }),
        NativePushService.REGISTRATION_TIMEOUT_MS,
      );
      this.pendingRegistration = done;
    });
  }

  /** Deliver an outcome to an `enable()` awaiting registration, if any. */
  private settleRegistration(outcome: { success: boolean; error?: string }): void {
    this.pendingRegistration?.(outcome);
  }

  private onActionPerformed(action: ActionPerformed): void {
    const data = (action?.notification?.data ?? {}) as Record<string, unknown>;
    const messageId = data['message_id'] ?? data['messageId'];
    if (messageId !== undefined && messageId !== null && `${messageId}` !== '') {
      console.log(`[NativePush] push tap → navigating to /messages/${messageId}`);
      void this.router.navigateByUrl(`/messages/${messageId}`);
    } else {
      console.warn('[NativePush] push tap had no message id in data payload; not navigating');
    }
  }

  private async invalidateNativeTokens(): Promise<void> {
    const list = await firstValueFrom(this.deviceTokenService.getDeviceTokens());
    const nativeTokens = list.device_tokens.filter(
      token => (token.device_type === 'android' || token.device_type === 'ios') && token.is_active,
    );
    for (const token of nativeTokens) {
      try {
        await firstValueFrom(this.deviceTokenService.invalidateDeviceToken(token.id));
      } catch (err) {
        console.warn('[NativePush] failed to invalidate token', token.id, err);
      }
    }
  }

  private setPermission(permission: PermissionState): void {
    this.status.next({ ...this.status.value, permission });
  }

  /**
   * Deep-link the user to this app's OS notification settings so someone who
   * previously DENIED the permission can turn it back on. iOS never re-prompts
   * once denied, so `requestPermissions()` is a dead end there — the only way
   * forward is Settings. iOS honours the `app-settings:` URL scheme, which opens
   * straight to the app's page in Settings. No-op on the web; best-effort (and
   * harmless) on Android, where the blocked UI shows manual steps instead.
   */
  async openAppSettings(): Promise<void> {
    if (!isNativeApp()) {
      return;
    }
    try {
      window.open('app-settings:', '_system');
    } catch (err) {
      console.error('[NativePush] failed to open app settings', err);
    }
  }

  /** Breadcrumb: `register()` was invoked (from init auto-register or enable). */
  private trackRegisterInvoked(trigger: 'init' | 'enable'): void {
    this.appInsights.trackEvent('native_push_register_invoked', {
      platform: Capacitor.getPlatform(),
      trigger,
      permission: this.status.value.permission,
    });
  }

  /**
   * Emit a single `native_push_enable` custom event capturing the outcome of an
   * enable() attempt so every success/failure is queryable in App Insights
   * customEvents. For a `registration-error` outcome the serialized underlying
   * payload (captured on the listener) is attached so the real APNs/FCM reason
   * is visible remotely. Token VALUES are never logged.
   */
  private trackEnableOutcome(
    outcome: string,
    opts: { permissionState: PermissionState | null; error?: unknown },
  ): void {
    const props: Record<string, unknown> = {
      platform: Capacitor.getPlatform(),
      outcome,
      permissionState: opts.permissionState,
    };
    if (outcome === 'registration-error' && this.lastRegistrationErrorDetail) {
      props['registrationErrorDetail'] = this.lastRegistrationErrorDetail;
    }
    if (opts.error !== undefined) {
      const serialized = this.serializeUnknownError(opts.error);
      props['errorDetail'] = serialized.detail;
      if (serialized.message) {
        props['errorMessage'] = serialized.message;
      }
    }
    this.appInsights.trackEvent('native_push_enable', props);
  }

  /**
   * Best-effort, privacy-safe serialization of an `unknown` error payload for
   * telemetry: pulls a `message`/`code` when present and a bounded stringified
   * form. Never includes token values (callers only pass error objects here).
   */
  private serializeUnknownError(err: unknown): {
    message?: string;
    code?: string | number;
    detail: string;
  } {
    const truncate = (value: string): string =>
      value.length > 500 ? `${value.slice(0, 500)}…` : value;

    if (err instanceof Error) {
      return { message: err.message, detail: truncate(`${err.name}: ${err.message}`) };
    }
    if (err && typeof err === 'object') {
      const obj = err as Record<string, unknown>;
      // Capacitor's `registrationError` payload carries the native
      // NSError.localizedDescription under an `error` string property; fall back
      // to a conventional `message` field for any other shape.
      const message =
        typeof obj['error'] === 'string'
          ? (obj['error'] as string)
          : typeof obj['message'] === 'string'
            ? (obj['message'] as string)
            : undefined;
      const rawCode = obj['code'];
      const code =
        typeof rawCode === 'string' || typeof rawCode === 'number' ? rawCode : undefined;
      let stringified: string;
      try {
        stringified = JSON.stringify(obj);
      } catch {
        stringified = String(err);
      }
      const detail = stringified && stringified !== '{}' ? stringified : String(err);
      return { message, code, detail: truncate(detail) };
    }
    return { detail: truncate(String(err)) };
  }
}
